import { useMemo, useEffect, useState, useCallback } from "react"
import { Client } from "../../../ducks/client/types"
import { TableHeaderItem, TablePagination, TableSortBy } from "../../../hooks/useTable"
import { useDispatch, useSelector } from "react-redux";
import { clientSelectors } from "../../../ducks/client/selectors";
import { AppDispatch } from "../../../store";
import { clientActions } from "../../../ducks/client";
import { ClickableText } from "../../atoms/ClickableText";
import { TableCheckbox } from "../../atoms/TableCheckbox";
import { usePermissions } from "../../../hooks/usePermissions";
import { Resource, Permission } from "../../../constants/permissions";
import { ToastType } from "../../atoms/Toast";
import { getRoleDisplayName } from "../../../utils/roleMapping";

export const useClientPage = (onPermissionError?: (message: string) => void) => {
    const dispatch = useDispatch<AppDispatch>();
    const { canUpdate } = usePermissions();

    const clients = useSelector(clientSelectors.clients) ?? [];
    const selectedClient = useSelector(clientSelectors.selectedClient);
    const pageSize = useSelector(clientSelectors.pageSize);
    const totalItems = useSelector(clientSelectors.totalItems);
    const totalPages = useSelector(clientSelectors.totalPages);

    // Local pagination state
    const [currentPage, setCurrentPage] = useState(1);

    // Search state
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Single useEffect to handle all data fetching
    useEffect(() => {
        dispatch(clientActions.fetchClients.started({
            page: currentPage,
            page_size: pageSize,
            search: debouncedSearchTerm || undefined,
            onComplete: undefined,
            onError: undefined,
            onSuccess: undefined,
        }));
    }, [dispatch, currentPage, pageSize, debouncedSearchTerm]);

    // Modal logic
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

    const handleOpenDetailModal = useCallback((client: Client) => {
        setIsDetailModalOpen(true);
        // Fetch client details
        dispatch(clientActions.getClientDetails.started({
            id: client.id,
            onComplete: undefined,
            onError: undefined,
            onSuccess: undefined,
        }));
    }, [dispatch]);

    const handleCloseDetailModal = useCallback(() => {
        setIsDetailModalOpen(false);
    }, []);

    const handleReadStatusChange = useCallback((clientId: string, checked: boolean, onPermissionError?: (message: string) => void) => {
        // Check if user has permission to update clients
        if (!canUpdate(Resource.CLIENTS)) {
            const errorMessage = `You don't have permission to edit clients. Please contact your administrator.`;
            onPermissionError?.(errorMessage);
            return;
        }

        dispatch(clientActions.updateClientReadStatus.started({
            id: clientId,
            read: checked,
            onComplete: undefined,
            onError: (error) => {
                console.error('Error updating read status:', error);
            },
            onSuccess: () => {

            },
        }));
    }, [dispatch, canUpdate]);

    const clientsWithNo = clients.map((item, idx) => ({
        ...item,
        no: (currentPage - 1) * pageSize + idx + 1,
    }));

    const fetchDataFunction = useCallback((sort: TableSortBy, pagination?: TablePagination) => {
        const fetchPage = pagination?.page ?? currentPage;
        const fetchPageSize = pagination?.size ?? pageSize;
        dispatch(clientActions.fetchClients.started({
            page: fetchPage,
            page_size: fetchPageSize,
            search: debouncedSearchTerm || undefined,
            onComplete: undefined,
            onError: undefined,
            onSuccess: undefined,
        }));
    }, [dispatch, currentPage, pageSize, debouncedSearchTerm]);

    const tableHeaders: TableHeaderItem<Client & { no: number }>[] = useMemo(() => [
        {
            id: 1,
            title: "No",
            dataKey: "no",
        },
        {
            id: 2,
            title: "Inquiry Number",
            dataKey: "inquiry_number",
            popupTitle: "Inquiry Number",
            dataMapFunction: (client) => ({
                text: client.inquiry_number,
                onClick: () => handleOpenDetailModal(client),
            }),
            customComponent: ClickableText,
        },
        {
            id: 3,
            title: "Name",
            dataKey: "username",
            dataMapFunction: (client) => client.username || `${client.first_name} ${client.last_name}`.trim() || "N/A"
        },
        {
            id: 4,
            title: "Inquiry Type",
            dataKey: "inquiry_type",
            popupTitle: "Inquiry Type",
            dataMapFunction: (client) => client.inquiry_type ? client.inquiry_type.charAt(0).toUpperCase() + client.inquiry_type.slice(1) : "N/A"
        },
        {
            id: 5,
            title: "Role",
            dataKey: "role",
            dataMapFunction: (client) => getRoleDisplayName(client.role)
        },
        { id: 6, title: "Email", dataKey: "email_address" },
        { id: 7, title: "Phone Number", dataKey: "phone_number" },
        {
            id: 8,
            title: "",
            dataKey: "read",
            dataMapFunction: (client) => ({
                checked: client.read,
                onChange: (checked: boolean) => handleReadStatusChange(client.id, checked, onPermissionError),
                disabled: !canUpdate(Resource.CLIENTS), // Disable checkbox if user can't update
            }),
            customComponent: TableCheckbox,
        },
    ], [handleOpenDetailModal, handleReadStatusChange]);

    // Add a handler to change page and fetch data
    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    // Search handler
    const handleSearchChange = useCallback((value: string) => {
        setSearchTerm(value);
        // Reset to first page when searching
        setCurrentPage(1);
    }, []);

    // Reset to first page when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm]);

    // Only show pagination if there's more than one page
    const hasPagination = totalPages > 1;

    return {
        tableHeaders,
        clients: clientsWithNo,
        fetchDataFunction,
        isDetailModalOpen,
        selectedClient,
        handleOpenDetailModal,
        handleCloseDetailModal,
        searchTerm,
        handleSearchChange,
        page: currentPage,
        pageSize,
        totalItems,
        hasPagination,
        handlePageChange,
    }
}

export const useClientToast = () => {
    const [toast, setToast] = useState<{
        message: string;
        type: ToastType;
        visible: boolean;
    }>({ message: "", type: "success", visible: false });

    const showToast = (message: string, type: ToastType = "success") => {
        setToast({ message, type, visible: true });
        setTimeout(() => {
            setToast((t) => ({ ...t, visible: false }));
        }, 2000);
    };

    const hideToast = () => setToast((t) => ({ ...t, visible: false }));

    return {
        toast,
        showToast,
        hideToast,
    };
};